import { useCallback, useMemo } from 'react';
import { MessageMention } from '@connect-x-app/shared-domain-models';
import { hasOpenTasks } from '@connect-x-app/shared-utils';
import { ChatActionsResult } from '@connect-x-app/shared-view-models';

import { useExternalMentionsStore, useMessageSessionStore, useQuickReplyStore } from '../../stores';
import { useAuthentication } from '../useAuthentication';
import { useSendChatMessage } from '../useSendChatMessage';
import { useChannels } from './useChannels';
import { useFlightRooms } from './useFlightRooms';

export const useChatMessage = (): ChatActionsResult => {
  const { selectedFlightRoom } = useFlightRooms();
  const { selectedChannel } = useChannels();
  const { clearMessage, clearSelectedMentions, message, selectedMentions, setMessage, setSelectedMentions } =
    useMessageSessionStore();

  const { canMentionExternally, isUserPartOfChannel } = useAuthentication();
  const { externalMentions } = useExternalMentionsStore();

  const isChannelPreviewMode = !!selectedChannel && !isUserPartOfChannel(selectedChannel);

  const { remove, retry, send } = useSendChatMessage();

  const { quickReplies } = useQuickReplyStore();

  const reset = useCallback(() => {
    clearMessage();
    clearSelectedMentions();
  }, [clearMessage, clearSelectedMentions]);

  const selectMention = useCallback(
    (mention: MessageMention) => {
      setSelectedMentions(prevState => [...prevState, mention]);
    },
    [setSelectedMentions],
  );

  const selectedMentionsFiltered = useMemo(() => {
    if (canMentionExternally) {
      return selectedMentions;
    }
    const externalLabels = new Set(externalMentions.map(m => m.label));
    return selectedMentions.filter(m => !externalLabels.has(m.label));
  }, [canMentionExternally, externalMentions, selectedMentions]);

  const handleQuickReply = useCallback(
    (quickReply: string) => {
      if (message) {
        return setMessage(`${message} ${quickReply}`);
      }
      return setMessage(quickReply);
    },
    [message, setMessage],
  );

  return {
    clearSelectedMentions,
    handleQuickReply,
    isChannelPreviewMode,
    isPreviewMode: hasOpenTasks(selectedFlightRoom?.tasks),
    message,
    quickReplies,
    remove,
    reset,
    retry,
    selectMention,
    selectedMentions: selectedMentionsFiltered,
    send,
    setMessage,
  };
};
